import React from "react";

import { faPlay } from "@fortawesome/pro-regular-svg-icons";
import { useFormContext, useWatch } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { PreviewQueryRequestEnvironmentEnum } from "src/clients/features-control";
import { PreviewData } from "src/featureCatalogue/Queries/Create/helpers";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { UseFeatureQueryPreview } from "src/featureCatalogue/queries";
import { useCapabilities } from "src/hooks/useCapabilities";
import { logger } from "src/utils/logger";

export const RunQueryButton: React.FC<{
  featurePreview: UseFeatureQueryPreview;
  disabled?: boolean;
  handleFeatureGeneration?: (previewData: PreviewData) => void;
}> = ({ featurePreview, disabled, handleFeatureGeneration }) => {
  const { featureQueriesCatalogue } = useCapabilities();
  const { getValues } = useFormContext<QueryFormValues>();

  const queryString = useWatch({ name: "query" });

  const handleRunQuery = async (
    environment: PreviewQueryRequestEnvironmentEnum,
  ) => {
    try {
      const [queryString, windowLength, endTime] = getValues([
        "query",
        "window_length",
        "end_datetime",
      ]);

      if (!queryString.length) return;

      await featurePreview.triggerPreview({
        queryString,
        windowLength,
        environment,
        endTime,
      });

      // Wait for the preview data to be ready and get it from the promise
      const previewData = await featurePreview.promise.current?.promise;

      if (previewData?.data && handleFeatureGeneration) {
        handleFeatureGeneration(previewData.data);
      }
    } catch (error) {
      logger.error(error);
    }
  };

  const isLiveQueryRunning =
    featurePreview.isPending &&
    featurePreview.variables?.environment ===
      PreviewQueryRequestEnvironmentEnum.LIVE;

  const isSandboxQueryRunning =
    featurePreview.isPending &&
    featurePreview.variables?.environment ===
      PreviewQueryRequestEnvironmentEnum.SANDBOX;

  return (
    <div className="flex w-full justify-start gap-x-4">
      <Button
        dataLoc="run-query-button-sandbox"
        disabled={
          !queryString.length ||
          disabled ||
          featurePreview.isPending ||
          !featureQueriesCatalogue.canPreviewSandbox
        }
        iconLeft={faPlay}
        loading={isSandboxQueryRunning}
        variant="secondary"
        onClick={() => {
          if (!featurePreview.isPending) {
            handleRunQuery(PreviewQueryRequestEnvironmentEnum.SANDBOX);
          }
        }}
      >
        Run Query in Sandbox
      </Button>
      <Button
        dataLoc="run-query-button-live"
        disabled={
          !queryString.length ||
          disabled ||
          featurePreview.isPending ||
          !featureQueriesCatalogue.canPreviewLive
        }
        iconLeft={faPlay}
        loading={isLiveQueryRunning}
        variant="secondary"
        onClick={() => {
          if (!featurePreview.isPending) {
            handleRunQuery(PreviewQueryRequestEnvironmentEnum.LIVE);
          }
        }}
      >
        Run Query in Live
      </Button>
    </div>
  );
};
